
import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import AccessibleBreadcrumb from '@/components/navigation/AccessibleBreadcrumb';
import { generateEnhancedSchema, generateBreadcrumbData } from '@/utils/accessibleSEO';

const PrivacyPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  
  // References for each section for smooth scrolling
  const section1Ref = useRef<HTMLHeadingElement>(null);
  const section2Ref = useRef<HTMLHeadingElement>(null);
  const section3Ref = useRef<HTMLHeadingElement>(null);
  const section4Ref = useRef<HTMLHeadingElement>(null);
  const section5Ref = useRef<HTMLHeadingElement>(null);
  const section6Ref = useRef<HTMLHeadingElement>(null);
  const section7Ref = useRef<HTMLHeadingElement>(null);
  const section8Ref = useRef<HTMLHeadingElement>(null);
  
  // Function for smooth scrolling to sections
  const scrollToSection = (ref: React.RefObject<HTMLHeadingElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  useEffect(() => {
    // Update SEO meta tags based on current language
    const metaTitles = {
      de: 'Datenschutzerklärung | Future Level',
      en: 'Privacy Policy | Future Level',
      fr: 'Politique de Confidentialité | Future Level',
      it: 'Informativa sulla Privacy | Future Level'
    };
    
    const metaDescriptions = {
      de: 'Datenschutzerklärung - Future Level - Informationen zum Datenschutz und zur Verarbeitung personenbezogener Daten',
      en: 'Privacy Policy - Future Level - Information about data protection and processing of personal data',
      fr: 'Politique de Confidentialité - Future Level - Informations sur la protection des données et le traitement des données personnelles',
      it: 'Informativa sulla Privacy - Future Level - Informazioni sulla protezione dei dati e sul trattamento dei dati personali'
    };
    
    const pagePath = currentLanguage === 'de' ? '/datenschutz' : 
                    currentLanguage === 'en' ? '/privacy-policy' : 
                    currentLanguage === 'fr' ? '/politique-de-confidentialite' : '/informativa-privacy';
    
    const canonicalUrl = getCanonicalUrl(pagePath.substring(1), currentLanguage);
    
    updateMetaTags({
      title: metaTitles[currentLanguage],
      description: metaDescriptions[currentLanguage],
      keywords: "Datenschutz, Privacy Policy, DSGVO, Cookies, personenbezogene Daten, Future Level",
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogType: "article"
    });
    
    // Generate schema markup for the privacy content
    const breadcrumbItems = generateBreadcrumbData(
      pagePath,
      currentLanguage === 'de' ? 'Datenschutz' : 
      currentLanguage === 'en' ? 'Privacy Policy' :
      currentLanguage === 'fr' ? 'Politique de Confidentialité' : 'Informativa sulla Privacy',
      currentLanguage
    );
    
    generateEnhancedSchema({
      title: metaTitles[currentLanguage],
      description: metaDescriptions[currentLanguage],
      type: 'Article',
      publishDate: '2025-04-01',
      modifiedDate: '2025-04-15',
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      keywords: "Datenschutz, Privacy Policy, DSGVO, Cookies, personenbezogene Daten",
      breadcrumbItems: breadcrumbItems
    });
    
  }, [currentLanguage, t]);

  // Define breadcrumb items based on current language
  const breadcrumbItems = [
    {
      label: currentLanguage === 'de' ? 'Datenschutz' : 
             currentLanguage === 'en' ? 'Privacy Policy' :
             currentLanguage === 'fr' ? 'Politique de Confidentialité' : 'Informativa sulla Privacy'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main id="main-content" className="flex-grow py-8 md:py-16">
        <div className="container mx-auto px-4">
          <AccessibleBreadcrumb items={breadcrumbItems} className="mb-8" />
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-8 text-white">{t.privacy}</h1>
            
            {/* Table of Contents - with accessibility improvements */}
            <nav aria-labelledby="toc-heading" className="bg-black/20 backdrop-blur-sm p-6 rounded-lg mb-8 border border-purple-500/20 shadow-md">
              <h2 id="toc-heading" className="text-xl font-semibold mb-4 flex items-center text-white">
                {currentLanguage === 'de' ? 'Inhaltsverzeichnis' : 
                currentLanguage === 'en' ? 'Table of Contents' :
                currentLanguage === 'fr' ? 'Table des matières' : 'Indice'}
                <ChevronDown className="ml-2 h-5 w-5" aria-hidden="true" />
              </h2>
              <ol className="space-y-2 list-decimal list-inside text-gray-200">
                <li>
                  <button 
                    onClick={() => scrollToSection(section1Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Verantwortliche Stelle
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section2Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Erhebung und Verarbeitung personenbezogener Daten
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section3Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Verwendung von Cookies
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section4Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Newsletter
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section5Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Google Analytics
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section6Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Ihre Rechte
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section7Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Datensicherheit
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section8Ref)}
                    className="text-left text-gray-200 hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Änderungen dieser Datenschutzerklärung
                  </button>
                </li>
              </ol>
            </nav>
            
            {/* Privacy content with improved semantic structure and styling */}
            <div className="prose prose-invert max-w-none bg-black/20 backdrop-blur-sm p-8 rounded-lg border border-purple-500/20 shadow-md">
              <section id="section-1" aria-labelledby="section-title-1">
                <h2 id="section-title-1" ref={section1Ref} className="text-2xl font-bold mb-4 text-purple-400 scroll-mt-24">1. Verantwortliche Stelle</h2>
                <div className="bg-black/30 p-6 rounded-md border-l-4 border-purple-500 mb-6">
                  <p className="mb-0 text-gray-200">
                    Verantwortlich für die Datenverarbeitung auf dieser Website ist:<br />
                    Future Level GmbH<br />
                    Musterstrasse 123<br />
                    8000 Zürich<br />
                    Schweiz<br />
                    E-Mail: info@future-level.ch<br />
                    Telefon: +41 41 539 16 13
                  </p>
                </div>
              </section>
              
              <section id="section-2" aria-labelledby="section-title-2">
                <h2 id="section-title-2" ref={section2Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">2. Erhebung und Verarbeitung personenbezogener Daten</h2>
                <p className="mb-6 text-gray-200">
                  Wir erheben personenbezogene Daten, wenn Sie uns diese im Rahmen Ihrer Bestellung, bei einer Kontaktaufnahme 
                  oder bei Registrierung für unseren Newsletter mitteilen. Welche Daten erhoben werden, ist aus den jeweiligen 
                  Eingabeformularen ersichtlich. Wir verwenden die von Ihnen mitgeteilten Daten zur Vertragsabwicklung und 
                  Bearbeitung Ihrer Anfragen.
                </p>
              </section>
              
              <section id="section-3" aria-labelledby="section-title-3">
                <h2 id="section-title-3" ref={section3Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">3. Verwendung von Cookies</h2>
                <p className="mb-6 text-gray-200">
                  Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die auf Ihrem Endgerät gespeichert werden 
                  und die bestimmte Einstellungen und Daten zum Austausch mit unserem System über Ihren Browser speichern. 
                  Einige der von uns verwendeten Cookies werden nach Ende der Browser-Sitzung, also nach Schließen Ihres 
                  Browsers, wieder gelöscht (sog. Sitzungs-Cookies). Andere Cookies verbleiben auf Ihrem Endgerät und 
                  ermöglichen uns, Ihren Browser beim nächsten Besuch wiederzuerkennen (persistente Cookies).
                </p>
              </section>
              
              <section id="section-4" aria-labelledby="section-title-4">
                <h2 id="section-title-4" ref={section4Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">4. Newsletter</h2>
                <div className="bg-black/30 p-6 rounded-md mb-6">
                  <p className="mb-0 text-gray-200">
                    Wenn Sie den auf der Website angebotenen Newsletter beziehen möchten, benötigen wir von Ihnen eine 
                    E-Mail-Adresse sowie Informationen, welche uns die Überprüfung gestatten, dass Sie der Inhaber der 
                    angegebenen E-Mail-Adresse sind und mit dem Empfang des Newsletters einverstanden sind. Diese Daten 
                    verwenden wir ausschließlich für den Versand der angeforderten Informationen und geben diese nicht 
                    an Dritte weiter. Die erteilte Einwilligung zur Speicherung der Daten, der E-Mail-Adresse sowie deren 
                    Nutzung zum Versand des Newsletters können Sie jederzeit widerrufen.
                  </p>
                </div>
              </section>
              
              <section id="section-5" aria-labelledby="section-title-5">
                <h2 id="section-title-5" ref={section5Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">5. Google Analytics</h2>
                <p className="mb-6 text-gray-200">
                  Diese Website benutzt Google Analytics, einen Webanalysedienst der Google Inc. („Google"). Google 
                  Analytics verwendet sog. „Cookies", Textdateien, die auf Ihrem Computer gespeichert werden und die 
                  eine Analyse der Benutzung der Website durch Sie ermöglichen. Wir verarbeiten Ihre Daten auf Grundlage 
                  unseres berechtigten Interesses an der wirtschaftlichen Führung unseres Unternehmens (Art. 6 Abs. 1 
                  lit. f DSGVO). Sie können die Speicherung der Cookies durch eine entsprechende Einstellung Ihrer 
                  Browser-Software verhindern.
                </p>
              </section>
              
              <section id="section-6" aria-labelledby="section-title-6">
                <h2 id="section-title-6" ref={section6Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">6. Ihre Rechte</h2>
                <div className="bg-black/30 p-6 rounded-md border-l-4 border-purple-500 mb-6">
                  <p className="mb-0 text-gray-200">
                    Sie haben das Recht, jederzeit Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu 
                    erhalten. Ebenso haben Sie das Recht auf Berichtigung, Sperrung oder, abgesehen von der 
                    vorgeschriebenen Datenspeicherung zur Geschäftsabwicklung, Löschung Ihrer personenbezogenen Daten. 
                    Sie können auch die Einschränkung der Verarbeitung verlangen, der Verarbeitung widersprechen oder 
                    sich bei einer Aufsichtsbehörde beschweren.
                  </p>
                </div>
              </section>
              
              <section id="section-7" aria-labelledby="section-title-7">
                <h2 id="section-title-7" ref={section7Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">7. Datensicherheit</h2>
                <p className="mb-6 text-gray-200">
                  Wir sichern unsere Website und sonstigen Systeme durch technische und organisatorische Maßnahmen 
                  gegen Verlust, Zerstörung, Zugriff, Veränderung oder Verbreitung Ihrer Daten durch unbefugte Personen.
                </p>
              </section>
              
              <section id="section-8" aria-labelledby="section-title-8">
                <h2 id="section-title-8" ref={section8Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">8. Änderungen dieser Datenschutzerklärung</h2>
                <p className="mb-6 text-gray-200">
                  Wir behalten uns das Recht vor, diese Datenschutzerklärung jederzeit unter Beachtung der geltenden 
                  Datenschutzvorschriften zu ändern.
                </p>
              </section>
              
              <footer className="mt-12 pt-6 border-t border-gray-700 text-sm text-gray-400 flex items-center justify-between">
                <p>Stand: April 2025</p>
                <a 
                  href="#toc-heading" 
                  className="flex items-center text-purple-400 hover:text-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Zurück zum Anfang
                </a>
              </footer>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
